import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import FileStates from 'FileStates';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import DocumentActions from 'notebook/documents/DocumentActions';
import DocumentStore from 'notebook/documents/DocumentStore';
import AutoSaver from 'notebook/store/auto-save/AutoSaver';
import {
  WorksheetViewStateAction,
  WorksheetViewActionType,
  WorksheetViewState,
} from 'notebook/worksheets/WorksheetConstants';
import {WorksheetViewQuery} from 'notebook/worksheets/workspace/__generated/WorksheetViewQuery';
import WorksheetFileState from 'notebook/worksheets/workspace/WorksheetFileState';
import notifications from 'notifications';
import {Thunk} from 'ui-library/hooks/useReducerWithThunk';
import removeNulls from 'util/removeNulls';

type WorksheetOrInaccessibleGraphQL = WorksheetViewQuery['worksheet'];

export type WorksheetViewStateThunk = Thunk<WorksheetViewState, WorksheetViewStateAction>;

/**
 * A thunk action that fetches all documents in a worksheet.
 * Fetching the documents is an async operation, so we use a thunk to handle it.
 */
export function fetchAllDocumentsInWorksheet(
  worksheet: WorksheetOrInaccessibleGraphQL,
  {reloading}: {reloading?: boolean} = {}
): WorksheetViewStateThunk {
  return async (dispatch) => {
    if (!reloading) {
      // On initial load, we dispatch a loaded state with no steps to show the steps are loading.
      dispatch({type: WorksheetViewActionType.LOADED, worksheet, steps: null});
    }

    if (isInaccessible(worksheet)) {
      dispatch({type: WorksheetViewActionType.LOADED, worksheet, steps: []});
      return;
    }

    const docIds = worksheet.worksheetStepGroups.flatMap((group) =>
      group.worksheetSteps.map((step) => step.document.id)
    );

    // Get the worksheet file state to access its custom autosaver
    let customAutoSaver: AutoSaver | undefined;
    let customSaveHandler: (() => Promise<boolean>) | undefined;
    if (BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS) {
      const worksheetFileState = FileStates.get().findWhere({id: worksheet.id});
      if (worksheetFileState != null) {
        assert(
          worksheetFileState instanceof WorksheetFileState,
          'The worksheet file state is not a worksheet file state'
        );
        const autoSaver = worksheetFileState.autoSaver;
        if (autoSaver != null) {
          customAutoSaver = autoSaver;
          customSaveHandler = async () => {
            const result = await autoSaver.queueSave(true);
            return result ?? false;
          };
        }
      }
    }

    // Always trigger a fresh fetch for all documents.
    const fetchedAll = await DocumentActions.openMany(docIds, {
      customAutoSaver,
      relatedDocumentIdGetter: () => docIds,
      customSaveHandler,
    });
    if (!fetchedAll) {
      dispatch({type: WorksheetViewActionType.FAILED_TO_LOAD_STEPS});
      return;
    }

    // Once all documents are loaded, retrieve them from the store and update the state.
    const loadedDocs = removeNulls(docIds.map((docId) => DocumentStore.getDocument(docId)));

    if (loadedDocs.length !== docIds.length) {
      notifications.error('Failed to load all documents, please refresh the page.');
      dispatch({type: WorksheetViewActionType.FAILED_TO_LOAD_STEPS});
      return;
    }

    dispatch({type: WorksheetViewActionType.LOADED, worksheet, steps: loadedDocs});
  };
}
